import { computed, inject, Injectable, InjectionToken, OnDestroy, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NbJSThemeOptions, NbThemeService } from '@nebular/theme';
import { BaseEnvironemntConfig } from '@res/ng-shared';
import { colorSchemeDark, Theme, ThemeDefaultParams, themeQuartz } from 'ag-grid-community';

export const APP_CONFIG = new InjectionToken<BaseEnvironemntConfig>('App Config');
export const ALL_THEMES = new InjectionToken<Array<NbJSThemeOptions & { agGridTheme: Theme<ThemeDefaultParams> }>>(
  'All available themes',
);

@Injectable({ providedIn: 'root' })
export class CurrentThemeService implements OnDestroy {
  private alive = true;
  private environment = inject(APP_CONFIG);
  private allThemes = inject(ALL_THEMES);
  private rippleService = inject(NbThemeService);
  private themeService = inject(NbThemeService);
  private onThemeChangeSignal: Signal<{ name: string; previous: string }> = toSignal(this.themeService.onThemeChange());

  // readonly currentTheme = this.themeService.currentTheme;
  // readonly currentTheme$: Observable<string> = new Observable<string>((subscriber) => {
  //   subscriber.next(localStorage['theme']);
  // }).pipe(
  //   tap((x) => console.log('currentTheme$', x)),
  //   takeWhile(() => this.alive)
  // );

  readonly currentThemeWithExpiration: Signal<string> = computed(
    () => this.onThemeChangeSignal() && localStorage['theme'],
  );

  readonly getAgGridTheme: Signal<Theme<ThemeDefaultParams>> = computed(() => {
    const themeChangeSignal = this.onThemeChangeSignal();
    // console.log({ themeChangeSignal });
    // const themeName = this.currentThemeSignal();
    return (
      this.allThemes.find((x) => x.name === themeChangeSignal.name)?.agGridTheme ??
      themeQuartz.withPart(colorSchemeDark)
    );
  });

  readonly currentThemeSignal: Signal<string> = computed(() => {
    const locValue = this.onThemeChangeSignal();

    // console.log('currentThemeSignal', { locValue });

    return locValue.name;
  });

  readonly isMaterialTheme: Signal<boolean> = computed(() => {
    return this.currentThemeSignal().startsWith('material');
  });

  setCurrentTheme(themeName: string): void {
    this.themeService.changeTheme(themeName);
    // console.log('setCurrentTheme: ', themeName);
    const currentTheme = {
      themeName,
      expires_in: this.calculateExpiration(Number(this.environment?.currentThemeLife)),
    };

    localStorage.setItem('theme', JSON.stringify(currentTheme));
  }

  initialize(): void {
    // console.log(
    //   `window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)')`,
    //   window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)')
    // );
    const userPrefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    const deviceTheme = userPrefersDark ? 'material-dark' : 'material-light';
    const themeName = localStorage['theme'] ? JSON.parse(localStorage['theme']).themeName : deviceTheme;
    this.themeService.changeTheme(themeName);
  }

  getCurrentTheme(): string {
    const userPrefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    const deviceTheme = userPrefersDark ? 'material-dark' : 'material-light';
    const themeName = localStorage['theme'] ? JSON.parse(localStorage['theme']).themeName : deviceTheme;
    this.themeService.changeTheme(themeName);
    return themeName;
  }

  calculateExpiration(iat: number): number {
    const currentDate = new Date().getTime();
    const timestamp = iat || Math.floor(Date.now() / 1000);

    return Math.floor(timestamp + currentDate);
  }

  ngOnDestroy(): void {
    this.alive = false;
  }
}
